/**
 * Today / My Day (design 5A "my-day"): the signed-in practitioner's own
 * schedule for today — real bookings from the clinic-wide window query
 * filtered to their uid — with a Next marker, the derived Delivered chip,
 * home-visit address and package context. ZOY-685: a group occurrence the
 * practitioner leads is one row with its attendee count, never one row per
 * attendee. Under the day: the practitioner's own past sessions that still
 * have no report (UnrecordedSessions). The "needs attention" card reads
 * the clinic-wide unpaid queue size from the real delivered-session summary
 * (ZOY-262), and exists only for roles that can see sessions (ZOY-322).
 */

import { Link } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useQuery } from '@tanstack/react-query';
import s from '../access/access.module.css';
import { Button, Card, Chip, Notice, Skeleton } from '@/ui/kit';
import { useAuthz } from '@/core/authz/AuthzProvider';
import { useSession } from '@/core/auth/SessionProvider';
import { PERM } from '@/core/authz/permissions';
import { listPatientPackages } from '@/core/clinic/packagesApi';
import { useLocale } from '@/core/i18n/LocaleProvider';
import { formatWeekdayDate } from '@/core/calendar/format';
import { useClinicSettings } from '../patients/useClinicSettings';
import { useStaffNames } from '../assessments/useStaffNames';
import { AgendaList } from '../scheduling/AgendaList';
import { dayBounds, localDateISO, nextUpcoming } from '../scheduling/calendarMath';
import {
  useBookingsWindow,
  useGroupSessionsWindow,
  useRoomsList,
} from '../scheduling/useSchedulingData';
import { canRecordDelivery } from '../sessions/sessionRules';
import { bookingPatientName, collapseGroupItems } from '../scheduling/bookingRules';
import { OWING_STATUSES } from '@/core/clinic/sessionsApi';
import { listDeliveredSessions, sessionFilters } from '../sessions/sessionQueries';
import { UnrecordedSessions } from './UnrecordedSessions';

export function TodayPage() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const { session } = useSession();
  const { locale } = useLocale();
  const settings = useClinicSettings();
  const { nameOf } = useStaffNames();
  const rooms = useRoomsList();
  const myUid = session.status === 'signedIn' ? session.me.uid : null;
  const tz = settings?.timezone ?? 'Asia/Tehran';
  const cal = settings?.calendarSystem ?? 'jalali';
  const today = localDateISO(new Date(), tz);
  const bounds = dayBounds(today, tz);

  const canRead = authz.can(PERM.bookingsRead);
  const mine = useBookingsWindow(
    canRead && myUid
      ? { from: bounds.from, to: bounds.to, practitionerUid: myUid, status: 'all' }
      : null,
  );
  const groups = useGroupSessionsWindow(canRead && myUid ? bounds : null);
  const items = collapseGroupItems(
    mine.data ?? [],
    (groups.data?.items ?? []).filter((g) => g.practitionerUid === myUid),
  );
  const patientName = bookingPatientName;
  const next = nextUpcoming(items, new Date());
  const home = items.filter((b) => b.status === 'booked' && b.locationType === 'off_site').length;
  const live = items.filter((b) => b.status === 'booked').length;

  const dateLine = settings
    ? formatWeekdayDate(new Date(), { locale, calendar: cal, timezone: tz })
    : '…';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className={s.pageHead} style={{ marginBlockEnd: 0 }}>
        <h1 className={s.pageTitle}>{t('md.title')}</h1>
        <p className={s.pageSub}>
          {dateLine} · {t('md.sub')}
        </p>
      </div>

      {canRead ? (
        mine.isPending ? (
          <Skeleton height="120px" />
        ) : // ZOY-321: only a COLD failure — nothing ever loaded — replaces the
        // schedule. A failed background refresh keeps the last known schedule
        // on screen (isError alone covers both, and used to blank the day).
        mine.isLoadingError ? (
          <Notice
            kind="err"
            role="alert"
            action={<Button onClick={() => void mine.refetch()}>{t('rec.retry')}</Button>}
          >
            {t('rec.sectionFailed')}
          </Notice>
        ) : (
          <>
            <AgendaList
              items={items}
              tz={tz}
              head={dateLine}
              sub={t('md.summary', { n: live, home }) as string}
              // ZOY-321: one quiet standing line while the last refresh is
              // failing — no Notice, no live region, and nothing that stacks or
              // re-announces however many refreshes fail. It clears itself the
              // moment a refresh succeeds.
              note={
                mine.isRefetchError ? (
                  <>
                    <span>{t('md.stale')}</span>
                    <Button variant="link" onClick={() => void mine.refetch()}>
                      {t('rec.retry')}
                    </Button>
                  </>
                ) : null
              }
              nextId={next?.id ?? null}
              patientName={patientName}
              staffName={nameOf}
              roomName={(id) => (id ? (rooms.find((r) => r.id === id)?.name ?? null) : null)}
              showPractitioner={false}
              extra={(b) =>
                b.locationType === 'off_site' && b.visitAddress
                  ? `${b.visitAddress} — ${t('md.addressOnBooking')}`
                  : null
              }
              empty={t('md.empty') as string}
              // ZOY-551: the clinical record is written inside a STARTED
              // session, so Today no longer links past the start act. A
              // scheduled row still opens its booking, where Start lives.
              action={(b) =>
                authz.can(PERM.sessionsDeliver) && canRecordDelivery(b) ? (
                  <Link
                    to="/calendar/bookings/$bookingId/deliver"
                    params={{ bookingId: b.id }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button variant={Date.parse(b.endsAt) <= Date.now() ? 'brand' : 'quiet'}>
                      {t('ss.record')}
                    </Button>
                  </Link>
                ) : null
              }
            />
            <span style={{ fontSize: 12, color: 'var(--zc-muted)' }}>{t('ss.dayFoot')}</span>
            <PackageContextNote items={items} />
            {items.length === 0 && (
              <Notice
                kind="info"
                action={
                  <Link to="/calendar" search={{}}>
                    {t('md.openCalendar')}
                  </Link>
                }
              >
                {t('md.notPractitioner')}
              </Notice>
            )}
          </>
        )
      ) : null}

      <UnrecordedSessions />
      <NeedsAttentionCard />
    </div>
  );
}

/** Package line context for today's package-linked sessions (truthful counts). */
function PackageContextNote({
  items,
}: {
  items: readonly { patientId: string; packageItemId: string | null }[];
}) {
  const intl = useIntl();
  const authz = useAuthz();
  const ids = [...new Set(items.filter((b) => b.packageItemId).map((b) => b.patientId))];
  const first = ids[0];
  const q = useQuery({
    queryKey: ['packages', first],
    queryFn: () => listPatientPackages(first!),
    enabled: !!first && ids.length === 1 && authz.can(PERM.packagesRead),
  });
  if (!q.data) return null;
  const lines = items
    .map((b) => {
      for (const p of q.data.items) {
        const pr = p.progress.find((x) => x.itemId === b.packageItemId);
        if (pr)
          return intl.formatMessage(
            { id: 'md.packageLeft' },
            { name: p.name, n: intl.formatNumber(pr.remainingToSchedule) },
          );
      }
      return null;
    })
    .filter((x): x is string => x !== null);
  if (lines.length === 0) return null;
  return (
    <span style={{ fontSize: 12.5, color: 'var(--zc-muted)' }}>
      {[...new Set(lines)].join(' · ')}
    </span>
  );
}

/**
 * ZOY-322 (Product): the "Needs attention" card is the clinic-wide unpaid
 * queue, so it exists only for roles that can read sessions. Without
 * clinic.sessions.read it is absent — no empty or explanatory stand-in, just
 * as the Payments destination is absent from their navigation. With access:
 * the owed-session count and the way into the queue.
 */
export function NeedsAttentionCard() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string | number>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const canSessions = authz.can(PERM.sessionsRead);
  // One request, one row: the summary is over the full unpaid set.
  const unpaid = useQuery({
    queryKey: ['sessions', 'clinic', authz.clinicId, 'unpaid-count'],
    queryFn: () =>
      listDeliveredSessions({
        // Money still owed = unpaid OR partly paid (the account's projections).
        filters: sessionFilters({ paymentStatus: OWING_STATUSES }),
        sort: [{ field: 'deliveredAt', dir: 'asc' }],
        limit: 1,
        page: 1,
      }),
    enabled: canSessions,
    staleTime: 60_000,
  });
  if (!canSessions) return null;
  return (
    <div className={s.cardsRow}>
      <Card style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}
        >
          <strong style={{ color: 'var(--zc-teal-deep)', fontSize: 15 }}>
            {t('today.cardWorkTitle')}
          </strong>
          {unpaid.data && (
            <Chip kind={unpaid.data.summary.sessionCount > 0 ? 'warn' : 'ok'}>
              {intl.formatNumber(unpaid.data.summary.sessionCount)}
            </Chip>
          )}
        </div>
        <span style={{ color: 'var(--zc-ink-2)', fontSize: 13, lineHeight: 1.6 }}>
          {unpaid.data
            ? // ZOY-642: the plural argument must stay a number — `#` localises the digits.
              t('today.cardWorkUnpaid', { n: unpaid.data.summary.sessionCount })
            : unpaid.isError
              ? t('rec.sectionFailed')
              : t('common.loading')}
        </span>
        {unpaid.data && (
          <Link to="/payments" search={{ tab: 'unpaid' }}>
            <Button variant="quiet">{t('today.cardWorkOpen')}</Button>
          </Link>
        )}
      </Card>
    </div>
  );
}
